import { useEffect, useState } from "react";
import { TagAutocomplete } from "@/components/TagAutocomplete";
import { useCurrentUser } from "@/hooks/useAuth";
import { useFeatures } from "@/hooks/useFeatures";
import { useEffectiveIncludeImages, useUpdateUserSettings } from "@/hooks/useUserData";
import { usePreferences } from "@/store/preferences";
import {
  composeQuery,
  parseQuery,
  splitTagList,
  type FileType,
  type OrderKey,
} from "@/lib/query";

// Comma OR whitespace separates tags in the advanced form's text inputs, so
// the autocomplete's "last token" detector needs to know both. Compare to the
// classic search bar's default (`\S+$`), which is whitespace-only.
const COMMA_OR_SPACE_LAST_TOKEN = /[^\s,]+$/;

interface Props {
  initialQuery: string;
  onSearch: (query: string) => void;
}

const ANIMATED_FILE_TYPES: { value: FileType; label: string }[] = [
  { value: "", label: "All animations" },
  { value: "webm", label: "WebM" },
  { value: "gif", label: "GIF" },
  { value: "apng", label: "APNG" },
];

const IMAGE_FILE_TYPES: { value: FileType; label: string }[] = [
  { value: "image", label: "Images (any)" },
  { value: "jpg", label: "JPG" },
  { value: "png", label: "PNG" },
  { value: "webp", label: "WebP" },
];

const ORDER_KEYS: { value: OrderKey; label: string }[] = [
  { value: "", label: "Newest (default)" },
  { value: "id_desc", label: "Newest (id desc)" },
  { value: "id_asc", label: "Oldest first" },
  { value: "score", label: "Highest score" },
  { value: "score_asc", label: "Lowest score" },
  { value: "favcount", label: "Most favourited" },
  { value: "random", label: "Random" },
];

export function AdvancedSearch({ initialQuery, onSearch }: Props) {
  const seed = parseQuery(initialQuery);
  const [must, setMust] = useState(seed.must.join(" "));
  const [exclude, setExclude] = useState(seed.exclude.join(" "));
  const [type, setType] = useState<FileType>(seed.type);
  const [order, setOrder] = useState<OrderKey>(seed.order);
  const { data: features } = useFeatures();
  const { data: user } = useCurrentUser();
  const setIncludeImages = usePreferences((s) => s.setIncludeImages);
  const updateSettings = useUpdateUserSettings();
  const includeImages = useEffectiveIncludeImages();
  const imagesAvailable = features?.images_indexed === true;

  function handleIncludeImages(next: boolean) {
    if (user) {
      updateSettings.mutate({ include_images: next });
    } else {
      setIncludeImages(next);
    }
  }
  // Show image options when either the operator has indexed images AND the
  // user has them turned on, OR the seed query itself already targets one
  // (so editing an existing share-link doesn't drop the selection).
  const seedIsImage =
    seed.type === "image" ||
    seed.type === "jpg" ||
    seed.type === "png" ||
    seed.type === "webp";
  const showImageTypes = imagesAvailable && (includeImages || seedIsImage);
  const fileTypeOptions = showImageTypes
    ? [...ANIMATED_FILE_TYPES, ...IMAGE_FILE_TYPES]
    : ANIMATED_FILE_TYPES;

  // If the URL changes from outside (e.g. tag click), reseed the form.
  useEffect(() => {
    const parsed = parseQuery(initialQuery);
    setMust(parsed.must.join(" "));
    setExclude(parsed.exclude.join(" "));
    setType(parsed.type);
    setOrder(parsed.order);
  }, [initialQuery]);

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    const query = composeQuery({
      must: splitTagList(must),
      exclude: splitTagList(exclude),
      type,
      order,
    });
    onSearch(query);
  }

  return (
    <form onSubmit={submit} className="space-y-3 bg-surface-1 border border-surface-3 rounded-lg p-4">
      <Field label="Must contain" hint="space- or comma-separated">
        <TagAutocomplete
          value={must}
          onChange={setMust}
          onCommit={() => submit()}
          placeholder="canine animated"
          inputClassName={inputClass}
          tokenPattern={COMMA_OR_SPACE_LAST_TOKEN}
        />
      </Field>

      <Field label="Must NOT contain" hint="will be sent as -tag">
        <TagAutocomplete
          value={exclude}
          onChange={setExclude}
          onCommit={() => submit()}
          placeholder="cat fox"
          inputClassName={inputClass}
          tokenPattern={COMMA_OR_SPACE_LAST_TOKEN}
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="File type">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as FileType)}
            className={inputClass}
          >
            {fileTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Order">
          <select
            value={order}
            onChange={(e) => setOrder(e.target.value as OrderKey)}
            className={inputClass}
          >
            {ORDER_KEYS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
      </div>

      {imagesAvailable && (
        <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs text-zinc-400">
          <input
            type="checkbox"
            checked={includeImages}
            onChange={(e) => handleIncludeImages(e.target.checked)}
            disabled={updateSettings.isPending}
            className="accent-accent"
          />
          <span>Include static images (jpg / png / webp)</span>
        </label>
      )}

      <div className="flex items-center gap-3 pt-2 min-w-0">
        <button
          type="submit"
          className="shrink-0 bg-accent hover:bg-accent-hover text-white font-semibold px-5 py-2.5 min-h-[44px] rounded-lg transition-colors text-sm"
        >
          Search
        </button>
        <code className="text-xs text-zinc-500 font-mono truncate min-w-0 flex-1">
          {composeQuery({ must: splitTagList(must), exclude: splitTagList(exclude), type, order }) || "(empty)"}
        </code>
      </div>
    </form>
  );
}

const inputClass =
  "w-full bg-surface-2 border border-surface-3 rounded px-3 py-2 text-sm focus:outline-none focus:border-accent transition-colors placeholder-zinc-600";

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        {hint && <span className="text-xs text-zinc-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
